import React, { useEffect, useState } from 'react';
import { Container, Header, Card, Label, Button, Icon, Breadcrumb } from 'semantic-ui-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getModelsForBrand, formatUrlPart, SITE_BASE_URL } from '../services/api';
import TopSection from './TopSection';
import DisclaimerLink from './DisclaimerLink';
import '../index.css';

const BrandPage = () => {
  const { brand } = useParams();
  const [models, setModels] = useState([]);
  const [displayBrand, setDisplayBrand] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCards, setExpandedCards] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    getModelsForBrand(brand)
      .then(data => {
        setModels(data);
        if (data.length > 0 && data[0].transcripts.length > 0) {
          setDisplayBrand(data[0].transcripts[0].make);
        } else {
          setDisplayBrand(brand);
        }
      })
      .catch(err => console.error(err));
  }, [brand]);

  const toggleCardExpansion = (modelId, e) => {
    e.stopPropagation();
    setExpandedCards(prev => ({
      ...prev,
      [modelId]: !prev[modelId],
    }));
  };

  const handleSearch = () => {
    if (searchQuery.trim() !== '') {
      navigate(`/search/${formatUrlPart(searchQuery)}`);
    }
  };

  const baseUrl = SITE_BASE_URL.replace(/\/$/, '');
  const canonicalUrl = `${baseUrl}/brand/${formatUrlPart(brand)}`;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": displayBrand,
        "item": canonicalUrl
      }
    ]
  };

  const title = `${displayBrand} Models - RV Customer Support & Troubleshooting`;
  const description = `Resolved ${displayBrand} RV support calls and troubleshooting guides. Browse models and fix common issues with certified technician transcripts.`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${baseUrl}/bcrvlogo.png`} />
        <meta property="og:site_name" content="Blue Compass RV Technical Support" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <TopSection
        title={`${displayBrand} Customer Support`}
        showLogo={true}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleSearch={handleSearch}
      />
      <main>
      <Container className="container" style={{ padding: '20px', backgroundColor: '#ffffff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
          <Breadcrumb style={{ margin: 0 }}>
            <Breadcrumb.Section link onClick={() => navigate('/')}>
              Home
            </Breadcrumb.Section>
            <Breadcrumb.Divider icon="right angle" />
            <Breadcrumb.Section active>
              {displayBrand}
            </Breadcrumb.Section>
          </Breadcrumb>
          <DisclaimerLink />
        </div>

        <Header
          as="h2"
          textAlign="center"
          style={{
            marginTop: '40px',
            marginBottom: '30px',
            color: '#000',
            fontWeight: 'bold',
            fontSize: '2.5em',
          }}
        >
          Models
        </Header>
        <Card.Group itemsPerRow={3} stackable style={{ margin: '0 -10px' }}>
          {models.map(model => {
            const problems = model.transcripts.flatMap(tx =>
              tx.problems.map((problem, idx) => ({
                transcriptId: tx.transcriptId,
                text: problem,
                key: `${tx.transcriptId}-${idx}`,
              }))
            );
            const isExpanded = expandedCards[model._id] || false;
            const displayedProblems = isExpanded ? problems : problems.slice(0, 5);
            const hasMore = problems.length > 5;

            return (
              <Card
                key={model._id}
                raised
                className="custom-card"
                style={{
                  margin: '10px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                }}
                onClick={() => navigate(`/brand/${formatUrlPart(brand)}/model/${formatUrlPart(model._id)}`)}
              >
                <Card.Content>
                  <Card.Header>
                    <Link
                      to={`/brand/${formatUrlPart(brand)}/model/${formatUrlPart(model._id)}`}
                      style={{ color: '#007bff', textDecoration: 'none' }}
                      onClick={e => e.stopPropagation()}
                    >
                      {model._id}
                    </Link>
                  </Card.Header>
                  <Card.Meta style={{ color: '#555', marginTop: '8px' }}>
                    {model.count} Resolved Calls
                  </Card.Meta>
                  {problems.length > 0 && (
                    <Card.Description style={{ marginTop: '12px' }}>
                      <strong style={{ color: '#333' }}>Problems:</strong>
                      <Label.Group>
                        {displayedProblems.map(problem => (
                          <Link
                            key={problem.key}
                            to={`/brand/${formatUrlPart(brand)}/model/${formatUrlPart(model._id)}/${problem.transcriptId}`}
                            onClick={e => e.stopPropagation()}
                          >
                            <Label
                              basic
                              color="blue"
                              className="accent-label"
                              style={{ margin: '5px 2px', borderRadius: '4px', padding: '6px 12px' }}
                            >
                              {problem.text}
                            </Label>
                          </Link>
                        ))}
                      </Label.Group>
                      {hasMore && (
                        <Button
                          basic
                          fluid
                          size="tiny"
                          onClick={e => toggleCardExpansion(model._id, e)}
                          style={{ marginTop: '10px' }}
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? `Collapse problems for ${model._id}` : `Expand to view all problems for ${model._id}`}
                        >
                          {isExpanded ? (
                            <>
                              <Icon name="arrow up" /> View Less
                            </>
                          ) : (
                            <>
                              <Icon name="arrow down" /> View More
                            </>
                          )}
                        </Button>
                      )}
                    </Card.Description>
                  )}
                </Card.Content>
              </Card>
            );
          })}
        </Card.Group>
      </Container>
      </main>
    </>
  );
};

export default BrandPage;